import { InfoCircleOutlined } from "@ant-design/icons";
import { ACCESS_TYPE_BLOCK_ID } from "@p0security/shared/integrations/notifiers/constants";
import type {
  WebAlertBlock,
  WebBlock,
  WebDynamicSelectBlock,
  WebInputBlock,
  WebInputChoice,
  WebModalState,
  WebStaticSelectBlock,
  WebToggleBlock,
} from "@p0security/shared/integrations/notifiers/web-requests/types";
import { assertNever } from "@p0security/shared/types/index";
import {
  Alert,
  Flex,
  Input,
  Select,
  Spacing,
  Switch,
  Tag,
  Typography,
} from "@p0security/ui";
import { accessTypeIcon } from "components/Jit/Policy/accessTypeIcons";
import { FormRow, RowLabel } from "components/common/FormRow";
import { createContext, useCallback, useContext, useMemo } from "react";
import {
  Controller,
  type ControllerProps,
  type ControllerRenderProps,
  type FieldValues,
  useFormContext,
  useFormState,
} from "react-hook-form";
import { spacing } from "styles/spacing";

import { SearchSelect } from "./SearchSelect";
import {
  MAX_SELECT_OPTIONS,
  createFormRules,
  createOptionGroups,
  parseFormError,
} from "./Utils";

export type FetchOptionsArgs = { argId: string; query: string };

type BlockVariant = "compact" | "default";

const BlockVariantContext = createContext<BlockVariant>("default");

export const hasBlockBetaSelection = (
  block: WebBlock,
  values: WebModalState
): boolean => {
  if (block.type !== "static-select" && block.type !== "dynamic-select") {
    return false;
  }
  const selectedValue = values[block.id];
  if (!selectedValue) return false;

  const selectedValues = Array.isArray(selectedValue)
    ? selectedValue
    : [selectedValue];

  for (const value of selectedValues) {
    const option = block.options?.find((opt) => opt.value === value);
    if (option?.maturity && option.maturity !== "ga") {
      return true;
    }
  }

  return false;
};

export const BlocksRenderer: React.FC<{
  blocks: WebBlock[];
  onFetchOptions: (args: FetchOptionsArgs) => Promise<WebInputChoice[]>;
  watching: WebModalState;
  variant?: BlockVariant;
}> = ({ blocks, onFetchOptions, watching, variant = "default" }) => {
  return (
    <BlockVariantContext.Provider value={variant}>
      {blocks.map((block) => {
        const hasNonGaMaturity = hasBlockBetaSelection(block, watching);
        return (
          <BlockRenderer
            key={block.id}
            block={block}
            onFetchOptions={onFetchOptions}
            hasNonGaMaturity={hasNonGaMaturity}
          />
        );
      })}
    </BlockVariantContext.Provider>
  );
};

const BlockRenderer: React.FC<{
  block: WebBlock;
  onFetchOptions: (args: FetchOptionsArgs) => Promise<WebInputChoice[]>;
  hasNonGaMaturity: boolean;
}> = ({ block, onFetchOptions, hasNonGaMaturity }) => {
  switch (block.type) {
    case "static-select":
      return (
        <StaticSelectRenderer
          block={block}
          hasNonGaMaturity={hasNonGaMaturity}
        />
      );
    case "alert":
      return <AlertRenderer block={block} />;
    case "input":
      return <InputRenderer block={block} />;
    case "dynamic-select":
      return (
        <DynamicSelectRenderer
          block={block}
          onFetchOptions={onFetchOptions}
          hasNonGaMaturity={hasNonGaMaturity}
        />
      );
    case "toggle":
      return <ToggleRenderer block={block} />;
    default:
      assertNever(block);
      return <>Unsupported block type</>;
  }
};

export const BlockLayout: React.FC<{
  id: string;
  label: string;
  hint?: string;
  required?: boolean;
  multivalued?: boolean;
  hidden?: boolean;
  hasNonGaMaturity?: boolean;
  children: React.ReactNode | React.ReactNode[];
}> = ({
  id,
  children,
  label,
  hint,
  required,
  multivalued,
  hidden,
  hasNonGaMaturity,
}) => {
  const variant = useContext(BlockVariantContext);
  const { control } = useFormContext();
  const { errors } = useFormState({ control });
  const error = parseFormError(errors[id]);
  if (hidden) return null;

  if (variant === "compact") {
    return (
      <FormRow>
        <RowLabel width={30}>
          {label}
          {hasNonGaMaturity && (
            <Spacing asChild ml={4}>
              <Tag color="blue" bordered={false} icon={<InfoCircleOutlined />}>
                Beta
              </Tag>
            </Spacing>
          )}
          {required && <Typography.Text type="danger"> *</Typography.Text>}
        </RowLabel>
        <Flex vertical gap={4} className="flex-1 min-w-0">
          {children}
          {multivalued && (
            <Typography.Text type="secondary" className="text-xs">
              You can select up to {MAX_SELECT_OPTIONS} items.
            </Typography.Text>
          )}
          {error ? (
            <Typography.Text type="danger" className="text-xs">
              {error}
            </Typography.Text>
          ) : hint ? (
            <Typography.Text type="secondary" className="text-xs">
              {hint}
            </Typography.Text>
          ) : null}
        </Flex>
      </FormRow>
    );
  }

  return (
    <Flex vertical gap="small">
      <div>
        <Flex
          align="center"
          /* eslint-disable-line react/forbid-component-props */ style={{
            gap: spacing.scale.xs,
          }}
        >
          <Spacing asChild mb={0}>
            <Typography.Title level={5}>{label}</Typography.Title>
          </Spacing>

          {hasNonGaMaturity && (
            <Tag color="blue" bordered={false} icon={<InfoCircleOutlined />}>
              Beta
            </Tag>
          )}

          {required ? (
            <Typography.Text type="danger">*</Typography.Text>
          ) : (
            <Typography.Text type="secondary">(optional)</Typography.Text>
          )}
        </Flex>
        {multivalued && (
          <Typography.Text type="secondary">
            You can select up to {MAX_SELECT_OPTIONS} items.
          </Typography.Text>
        )}
      </div>
      {children}
      {error ? (
        <Typography.Text type="danger">{error}</Typography.Text>
      ) : hint ? (
        <Typography.Text type="secondary">{hint}</Typography.Text>
      ) : null}
    </Flex>
  );
};

const ToggleRenderer: React.FC<{
  block: WebToggleBlock;
}> = ({ block }) => {
  const { control } = useFormContext();
  const handleChange = useCallback(
    (field: ControllerRenderProps<FieldValues, string>) => (checked: boolean) =>
      field.onChange(checked ? "true" : "false"),
    []
  );

  const renderToggle = useCallback<ControllerProps["render"]>(
    ({ field }) => (
      <Switch
        {...field}
        value={field.value === "true"}
        onChange={handleChange(field)}
        /* eslint-disable-line react/forbid-component-props */ style={{
          width: "50px",
        }}
      />
    ),
    [handleChange]
  );

  return (
    <BlockLayout {...block}>
      <Controller
        name={block.id}
        control={control}
        render={renderToggle}
        rules={createFormRules(block)}
      />
    </BlockLayout>
  );
};

const DynamicSelectRenderer: React.FC<{
  block: WebDynamicSelectBlock;
  onFetchOptions: (args: FetchOptionsArgs) => Promise<WebInputChoice[]>;
  hasNonGaMaturity: boolean;
}> = ({ block, onFetchOptions, hasNonGaMaturity }) => {
  const { control } = useFormContext();
  const { errors } = useFormState({ control });

  const handleFetchOptions = useCallback(
    async (search: string): Promise<WebInputChoice[]> => {
      return onFetchOptions({ argId: block.id, query: search });
    },
    [block.id, onFetchOptions]
  );
  const renderSelect = useCallback<ControllerProps["render"]>(
    ({ field }) => (
      <SearchSelect
        {...field}
        placeholder={block.placeholder}
        value={block.value}
        mode={block.multivalued ? "multiple" : undefined}
        fetchOptions={handleFetchOptions}
        status={errors[block.id] ? "error" : undefined}
      />
    ),
    [
      block.id,
      block.multivalued,
      block.placeholder,
      block.value,
      errors,
      handleFetchOptions,
    ]
  );

  return (
    <BlockLayout {...block} hasNonGaMaturity={hasNonGaMaturity}>
      <Controller
        name={block.id}
        control={control}
        render={renderSelect}
        rules={createFormRules(block)}
      />
    </BlockLayout>
  );
};

const StaticSelectRenderer: React.FC<{
  block: WebStaticSelectBlock;
  hasNonGaMaturity: boolean;
}> = ({ block, hasNonGaMaturity }) => {
  const { control } = useFormContext();
  const { errors } = useFormState({ control });

  const options = useMemo(() => {
    if (block.id === ACCESS_TYPE_BLOCK_ID) {
      return block.options.map((option) => ({
        label: (
          <Flex align="center" gap="small">
            {accessTypeIcon(option.value)}
            <Typography.Text ellipsis>{option.label}</Typography.Text>
          </Flex>
        ),
        value: option.value,
        filterLabel: option.label,
      }));
    }
    return createOptionGroups(block.options);
  }, [block.id, block.options]);

  const renderSelect = useCallback<ControllerProps["render"]>(
    ({ field }) => (
      <Select
        {...field}
        showSearch
        filterOption={true}
        optionFilterProp="filterLabel"
        placeholder={block.placeholder}
        value={block.value}
        mode={block.multivalued ? "multiple" : undefined}
        status={errors[block.id] ? "error" : undefined}
        allowClear
        options={options}
      />
    ),
    [
      block.id,
      block.multivalued,
      block.placeholder,
      block.value,
      errors,
      options,
    ]
  );

  return (
    <BlockLayout {...block} hasNonGaMaturity={hasNonGaMaturity}>
      <Controller
        name={block.id}
        control={control}
        render={renderSelect}
        rules={createFormRules(block)}
      />
    </BlockLayout>
  );
};

const InputRenderer: React.FC<{
  block: WebInputBlock;
}> = ({ block }) => {
  const { control } = useFormContext();
  const { errors } = useFormState({ control });

  const renderInput = useCallback<ControllerProps["render"]>(
    ({ field }) => {
      const Component = block.multiline ? Input.TextArea : Input;
      return (
        <Component
          {...field}
          {...(block.multiline ? { rows: 4 } : {})}
          placeholder={block.placeholder}
          status={errors[block.id] ? "error" : undefined}
        />
      );
    },
    [block.multiline, block.placeholder, block.id, errors]
  );

  return (
    <BlockLayout {...block}>
      <Controller
        name={block.id}
        control={control}
        render={renderInput}
        rules={createFormRules(block)}
      />
    </BlockLayout>
  );
};

const AlertRenderer: React.FC<{ block: WebAlertBlock }> = ({ block }) => {
  return (
    <Alert
      message={block.label}
      description={block.placeholder}
      type={block.variant}
      showIcon
    />
  );
};
